import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Container from "@/components/base/Container";
import { navLinks, siteInfo } from "@/mocks/site";

interface SiteHeaderProps {
  transparent?: boolean;
}

export default function SiteHeader({ transparent = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !transparent || scrolled;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <Container>
        <div className="flex h-16 items-center justify-between gap-4 md:h-20">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${siteInfo.name}首页`}>
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                solid ? "bg-primary-500 text-background-50" : "bg-background-50 text-primary-600"
              }`}
            >
              <i className="ri-trophy-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg font-bold tracking-wide transition-colors ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                {siteInfo.name}
              </span>
              <span
                className={`mt-1 text-[10px] tracking-[0.24em] transition-colors ${
                  solid ? "text-foreground-500" : "text-background-200/80"
                }`}
              >
                KAWEI OBSERVER
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? solid
                        ? "bg-primary-100 text-primary-800"
                        : "bg-background-50/20 text-background-50"
                      : solid
                        ? "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                        : "text-background-100/90 hover:bg-background-50/15 hover:text-background-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/#subscribe"
              className={`hidden whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors md:inline-flex ${
                solid
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-50 text-primary-700 hover:bg-background-100"
              }`}
            >
              订阅解读
            </Link>
            <button
              type="button"
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className={`flex h-10 w-10 items-center justify-center rounded-md border transition-colors lg:hidden ${
                solid
                  ? "border-background-300 text-foreground-800 hover:bg-background-100"
                  : "border-background-50/40 text-background-50 hover:bg-background-50/15"
              }`}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </Container>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <Container>
            <nav aria-label="移动端导航" className="flex flex-col py-3">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-100 text-primary-800"
                        : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link
                to="/#subscribe"
                className="mt-2 rounded-md bg-primary-500 px-3 py-3 text-center text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                订阅解读
              </Link>
            </nav>
          </Container>
        </div>
      ) : null}
    </header>
  );
}